import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useDialogStore } from '../../../../store/dialogStore';
import {
  TournamentAdminService,
  isDuplicateRequest,
} from '../TournamentAdminService';
import {
  PccTransferPreview,
  PccTransferResult,
  TournamentOverviewRow,
} from '../TournamentAdmin.types';

interface Props {
  tournament: TournamentOverviewRow;
}

// The dialog body: scrollbar always drawn (macOS hides overlay bars) and a
// fade + hint while there is more content below, so the end is never mistaken.
const ScrollBody: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);

  const update = () => {
    const el = ref.current;
    if (!el) return;
    setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  };

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div
        ref={ref}
        onScroll={update}
        className="flex-1 min-h-0 overflow-y-scroll pr-3 grid gap-3 content-start [scrollbar-width:thin] [scrollbar-color:#9ca3af_#f3f4f6] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full"
      >
        {children}
      </div>
      {moreBelow && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent flex items-end justify-center pb-0.5">
          <span className="text-[11px] text-gray-500 flex items-center gap-1">
            <ChevronDownIcon className="w-3 h-3 animate-bounce" />
            weiter scrollen
          </span>
        </div>
      )}
    </div>
  );
};

const squadText = (players: number, substitutes: number) =>
  substitutes > 0
    ? `${players} Spieler + ${substitutes} Ersatz`
    : `${players} Spieler`;

// PC CADDIE answers a code per player; anything that is not OK is shown as a problem.
const isPlayerOk = (code?: string) =>
  (code ?? '').toUpperCase() === 'OK' || code === '0';

/**
 * "An PCC senden": shows what would be copied, lets the association pick the
 * receiving PC CADDIE event, asks for a confirmation and then reports PC
 * CADDIE's answer player by player.
 */
const SendToPccDialog: React.FC<Props> = ({ tournament }) => {
  const [preview, setPreview] = useState<PccTransferPreview | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [eventNumber, setEventNumber] = useState<string>('');
  const [confirmed, setConfirmed] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<PccTransferResult | null>(null);

  const closeDialog = useDialogStore((state) => state.closeDialog);

  useEffect(() => {
    TournamentAdminService.fetchPccPreview(tournament.id)
      .then(setPreview)
      .catch((error) => {
        if (isDuplicateRequest(error)) return;
        console.error('PC CADDIE Vorschau konnte nicht geladen werden:', error);
        setLoadFailed(true);
      });
  }, [tournament.id]);

  // "dd.mm.yyyy" -> "yyyy-mm-dd", the form PC CADDIE reports event dates in
  const isoDate = (dotted: string) => {
    const m = dotted.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
  };
  const onTournamentDate = (date: string | null) =>
    !!date &&
    date >= isoDate(tournament.startDate) &&
    date <= isoDate(tournament.endDate);
  const sortedEvents = [...(preview?.events ?? [])].sort(
    (a, b) =>
      Number(onTournamentDate(b.date)) - Number(onTournamentDate(a.date)),
  );

  const chosenEvent = preview?.events.find((e) => e.number === eventNumber);
  const canSend =
    !!preview &&
    preview.playersTotal > 0 &&
    !!chosenEvent &&
    confirmed &&
    !isSending;

  const send = async () => {
    if (!canSend) return;
    try {
      setIsSending(true);
      setResult(
        await TournamentAdminService.sendToPcc(tournament.id, eventNumber),
      );
    } catch (error) {
      // The axios interceptor already shows the server's message in a dialog.
      console.error('Übertragung an PC CADDIE fehlgeschlagen:', error);
    } finally {
      setIsSending(false);
    }
  };

  // ------------------------------------------------------------- result view
  if (result) {
    const failedPlayers = result.players.filter((p) => !isPlayerOk(p.code));
    const ok = result.status === 'success';
    return (
      <div className="text-sm flex flex-col gap-3 pt-6 -mx-16 px-1 max-h-[calc(100vh-14rem)]">
        <ScrollBody>
          <div
            className={`flex items-start gap-2 rounded-md border p-3 ${
              ok
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {ok ? (
              <CheckCircleIcon className="w-6 h-6 shrink-0" />
            ) : (
              <XCircleIcon className="w-6 h-6 shrink-0" />
            )}
            <div>
              <div className="font-bold">
                {ok
                  ? 'Übertragung an PC CADDIE abgeschlossen'
                  : 'Übertragung an PC CADDIE fehlgeschlagen'}
              </div>
              <div>{result.message}</div>
              {!ok && result.error && (
                <div className="text-xs mt-1 opacity-80">
                  Antwort von PC CADDIE: {result.error}
                </div>
              )}
            </div>
          </div>

          {ok && (
            <div className="text-xs text-gray-600">
              Turnier <b>{tournament.title}</b> → PC CADDIE Turnier Nr.{' '}
              <b>{result.eventNumber}</b>
              {chosenEvent?.name ? ` (${chosenEvent.name})` : ''} ·{' '}
              {result.playersSent} Spieler übermittelt
              {failedPlayers.length > 0 && (
                <span className="text-red-600 font-medium">
                  , {failedPlayers.length} mit Rückmeldung
                </span>
              )}
            </div>
          )}

          {result.players.length > 0 && (
            <div className="border rounded-md divide-y">
              {result.players.map((player, index) => {
                const fine = isPlayerOk(player.code);
                return (
                  <div
                    key={index}
                    className={`flex items-start gap-2 px-2 py-1.5 ${fine ? '' : 'bg-red-50'}`}
                  >
                    {fine ? (
                      <CheckCircleIcon className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
                    ) : (
                      <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium truncate">
                          {[player.firstname, player.lastname]
                            .filter(Boolean)
                            .join(' ') ||
                            player.persid ||
                            `Spieler ${index + 1}`}
                        </span>
                        {player.code && (
                          <span
                            className={`text-xs ${fine ? 'text-gray-500' : 'text-red-600 font-medium'}`}
                          >
                            {player.code}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600">
                        {player.persid && <span>{player.persid} · </span>}
                        {player.msg}
                        {player.detail_string
                          ? ` – ${player.detail_string}`
                          : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollBody>
        <div className="flex justify-end pt-2 border-t">
          <button
            type="button"
            onClick={closeDialog}
            className="px-3 py-2 rounded-md text-sm bg-[#52A445] text-white hover:bg-green-700"
          >
            Schließen
          </button>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ preview view
  return (
    <div className="text-sm flex flex-col gap-3 pt-6 -mx-16 px-1 max-h-[calc(100vh-14rem)]">
      <div className="font-bold text-base">An PC CADDIE senden</div>

      <ScrollBody>
        <div className="border rounded-md p-2 bg-gray-50 grid gap-0.5">
          <div className="font-medium">{tournament.title}</div>
          <div className="text-xs text-gray-600">
            Austragungsort: {tournament.venue ?? '—'} · {tournament.startDate}
            {tournament.endDate !== tournament.startDate
              ? ` – ${tournament.endDate}`
              : ''}
          </div>
        </div>

        {!preview && !loadFailed && (
          <div className="text-center py-6 text-gray-500">
            <ArrowPathIcon className="w-8 h-8 mx-auto mb-2 animate-spin" />
            PC CADDIE wird abgefragt ...
          </div>
        )}

        {loadFailed && (
          <div className="text-red-600 text-xs">
            Die Vorschau konnte nicht geladen werden. Bitte versuchen Sie es
            später erneut.
          </div>
        )}

        {preview && (
          <>
            <div>
              <div className="font-medium mb-1">
                Spieler bereit zur Übertragung: {preview.playersTotal}
              </div>
              {preview.playersTotal === 0 ? (
                <div className="text-xs text-red-600">
                  Für dieses Turnier sind noch keine Spieler gemeldet. Es gibt
                  nichts zu übertragen.
                </div>
              ) : (
                <div className="grid gap-0.5 text-xs text-gray-700 border rounded-md p-2">
                  {preview.clubs.map((club) => (
                    <div
                      key={club.clubNumberCountry}
                      className="flex justify-between gap-2"
                    >
                      <span>{club.name ?? club.clubNumberCountry}</span>
                      <span className="text-gray-500 whitespace-nowrap">
                        {squadText(club.players, club.substitutes)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {preview.playersTotal > 0 && (
              <div>
                <div className="font-medium mb-1">
                  Bitte wählen Sie das Turnier in PC CADDIE ({preview.venue}),
                  in das kopiert werden soll:
                </div>
                {preview.eventsStatus === 'unavailable' ? (
                  <div className="text-xs text-red-600">
                    PC CADDIE von {preview.venue} konnte nicht abgefragt werden.
                    Eine Übertragung ist im Moment nicht möglich. Bitte
                    versuchen Sie es später erneut oder wenden Sie sich an den
                    Club.
                    {preview.eventsError && (
                      <div className="mt-1 text-gray-500">
                        Details: {preview.eventsError}
                      </div>
                    )}
                  </div>
                ) : preview.events.length === 0 ? (
                  <div className="text-xs text-red-600">
                    In PC CADDIE von {preview.venue} ist für den{' '}
                    {tournament.startDate}
                    {tournament.endDate !== tournament.startDate
                      ? ` – ${tournament.endDate}`
                      : ''}{' '}
                    kein Turnier angelegt. Die Spieler können erst übertragen
                    werden, wenn der Club das Turnier dort angelegt hat.
                  </div>
                ) : (
                  <div className="grid gap-1 border rounded-md p-1">
                    {sortedEvents.map((event) => (
                      <label
                        key={`${event.id}-${event.number}`}
                        className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-gray-50 ${
                          eventNumber === event.number ? 'bg-green-50' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="pccEvent"
                          checked={eventNumber === event.number}
                          onChange={() => {
                            setEventNumber(event.number ?? '');
                            setConfirmed(false);
                          }}
                        />
                        <span className="flex-1">
                          <span className="font-medium">{event.name}</span>
                          <span className="text-xs text-gray-500">
                            {' '}
                            [{event.number}]
                            {event.category ? ` · ${event.category}` : ''}
                          </span>
                        </span>
                        <span
                          className={`text-xs whitespace-nowrap ${
                            onTournamentDate(event.date)
                              ? 'text-gray-500'
                              : 'text-amber-700'
                          }`}
                        >
                          {event.date}
                          {!onTournamentDate(event.date) && ' · anderes Datum'}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {chosenEvent && (
              <label className="flex items-start gap-2 text-xs border border-amber-200 bg-amber-50 rounded-md p-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <span>
                  Ich bestätige, dass <b>{preview.playersTotal} Spieler</b> aus{' '}
                  {preview.clubs.length} Clubs in das PC CADDIE Turnier{' '}
                  <b>{chosenEvent.name}</b> [{chosenEvent.number}] übertragen
                  werden sollen. Bereits vorhandene Einträge werden von PC
                  CADDIE gemeldet.
                </span>
              </label>
            )}
          </>
        )}
      </ScrollBody>

      <div className="flex justify-end gap-3 pt-2 border-t">
        <button
          type="button"
          onClick={closeDialog}
          disabled={isSending}
          className="px-3 py-2 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={send}
          disabled={!canSend}
          className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm text-white ${
            canSend
              ? 'bg-[#52A445] hover:bg-green-700'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {isSending ? (
            <ArrowPathIcon className="w-4 h-4 animate-spin" />
          ) : (
            <PaperAirplaneIcon className="w-4 h-4" />
          )}
          {isSending ? 'Wird übertragen ...' : 'Jetzt an PC CADDIE senden'}
        </button>
      </div>
    </div>
  );
};

export default SendToPccDialog;
